import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { ClipboardUtils } from "abweb/utils/clipboard/ClipboardUtils";
import { DateTimeUtils } from "abweb/utils/date/DateTimeUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { NumberUtils } from "abweb/utils/NumberUtils";
export class DataGridUtils {
  /**
   * Formats a canonical data type to a localized string.
   * @param value Canonical data type (string, boolean, number, Date)
   * @returns {string} Localized string.
   */
  static canonicalTypeToString(value) {
    if (value == null) {
      return "";
    }
    switch (typeof value) {
      case "string":
        return value;
      case "number":
        return NumberUtils.formatValue(value);
      case "boolean":
        return value.toString();
    }
    if (ClassUtils.isInstanceOf(value, Date)) {
      return DateTimeUtils.DATE_SHORT.format(value);
    }
    return value.toString();
  }

  /**
   * Utility function to move columns within an Array of current columns.
   * @param currentColumns Current list of top-level grid columns.
   * @param columnsToMove Array of columns to move. All columns in this Array must share the same parent column (or be null)
   * @param insertBeforeColumn Column within the common parent column before which to move the columnsToMove.
   * @return New Array of containing the moved columns. If null is returned, it means that the arguments were invalid.
   */
  static moveColumns(currentColumns, columnsToMove, insertBeforeColumn) {
    let topLevelColumns = currentColumns.slice();
    if (columnsToMove.length === 0) {
      DataGridUtils.LOGGER.warn(`moveColumns columnsToMove is empty`);
      return null;
    }
    let parentColumn = columnsToMove[0].parentColumn;
    let columns = parentColumn ? parentColumn.childColumns : topLevelColumns;
    if (insertBeforeColumn) {
      if (insertBeforeColumn.parentColumn !== parentColumn) {
        // Columns don't share same parentColumn
        DataGridUtils.LOGGER.warn(`moveColumns insertBeforeColumn does not have same parentColumn as columnsToMove`);
        return null;
      }
      if (columnsToMove.includes(insertBeforeColumn)) {
        // insertBeforeColumn cannot be one of the columns moved
        DataGridUtils.LOGGER.warn(`moveColumns columnsToMove includes insertBeforeColumn`);
        return null;
      }
    }
    let actualColumnsToRemove = [];
    for (let col of columnsToMove) {
      let index = columns.indexOf(col);
      if (index >= 0) {
        columns.splice(index, 1);
        actualColumnsToRemove.push(col);
      } else {
        if (parentColumn) {
          DataGridUtils.LOGGER.warn(`moveColumns column '${col.id}' in columnsToMove not found in parentColumn '${parentColumn.id}' childColumns`);
        } else {
          DataGridUtils.LOGGER.warn(`moveColumns column '${col.id}' in columnsToMove not found in top-level currentColumns`);
        }
      }
    }
    let index = insertBeforeColumn ? columns.indexOf(insertBeforeColumn) : columns.length;
    columns.splice(index, 0, ...actualColumnsToRemove);
    return topLevelColumns;
  }
  static scrollbarSize(recalc = false) {
    if (!DataGridUtils.sbSize || recalc) {
      let scrollDiv = document.createElement("div");
      scrollDiv.style.position = "absolute";
      scrollDiv.style.top = "-9999px";
      scrollDiv.style.width = "50px";
      scrollDiv.style.height = "50px";
      scrollDiv.style.overflow = "scroll";
      document.body.appendChild(scrollDiv);
      DataGridUtils.sbSize = scrollDiv.offsetWidth - scrollDiv.clientWidth;
      document.body.removeChild(scrollDiv);
    }
    return DataGridUtils.sbSize;
  }

  // Originally in AbQiGridUtils written by mfabre
  static selection(selectedItems, coordsToData, numRows, columnMap, rowNames, defaults, addColNames, addRecNums, addRowNames, hasRecNums) {
    let recNumHeader = " ";
    let columns = [];
    let columnsValues = [];
    let toSave = [];
    let columnNames = Array.from(columnMap.values()).map(value => value.label);
    if (selectedItems.length > 0) {
      // Whole rows or whole columns
      if (selectedItems[0].rowId === null || selectedItems[0].colId === null) {
        selectedItems.forEach((value, index) => {
          let rowId = value.rowId;
          let colId = value.colId;
          if (colId === null)
            //Case where whole row is selected
            {
              if (rowId !== null) {
                if (index === 0 && (addColNames || defaults)) {
                  let headerRow = addRecNums ? [recNumHeader].concat(columnNames) : (addRowNames || defaults) && rowNames ? [" "].concat(columnNames) : columnNames;
                  toSave.push(headerRow);
                }
                let preRow = coordsToData(rowId, rowId)[0];
                toSave.push(addRecNums ? [(rowId + 1).toString()].concat(preRow) : (addRowNames || defaults) && rowNames ? [rowNames[rowId]].concat(preRow) : preRow);
              }
            } else if (rowId === null)
            //Whole column case
            {
              let addColsHeader = addColNames || defaults;
              if (index === 0) {
                if (addRecNums || defaults && hasRecNums) {
                  columns.push(-1);
                  addColsHeader && columnsValues.push(recNumHeader);
                } else if ((addRowNames || defaults) && rowNames) {
                  columns.push(-2);
                  addColsHeader && columnsValues.push(" ");
                }
              }
              let columnMatch = columnMap.get(colId.toString());
              if (!columnMatch) {
                console.log("ERROR: Columns provided do not match grid columns");
              } else {
                columns.push(columnMatch.index);
                addColsHeader && columnsValues.push(colId);
              }
            }
        });
        if (columns.length > 0) {
          toSave = columnsValues.length > 0 ? [columnsValues] : [];
          for (let index = 0; index < numRows; index++) {
            let rowToSave = [];
            columns.forEach(value => {
              let row = coordsToData(index, index)[0];
              value === -1 ? rowToSave.push((index + 1).toString()) : value === -2 ? rowToSave.push(rowNames ? rowNames[index] : "") : rowToSave.push(row[value]);
            });
            toSave.push(rowToSave);
          }
        }
      } else {
        let initialItem = selectedItems[0];
        let finalItem = selectedItems[selectedItems.length - 1];
        let initialColMatch = columnMap.get(initialItem.colId.toString());
        let finalColMatch = columnMap.get(finalItem.colId.toString());
        if (initialColMatch === undefined || finalColMatch === undefined) {
          console.log("ERROR: Columns provided do not match grid columns");
        } else {
          let initialCol = initialColMatch.index;
          let finalCol = finalColMatch.index;
          coordsToData(initialItem.rowId, finalItem.rowId).forEach((row, index) => {
            if (index === 0 && addColNames) {
              let preHeaderRow = columnNames.slice(initialCol, finalCol + 1);
              let headerRow = addRecNums ? [recNumHeader].concat(preHeaderRow) : addRowNames && rowNames ? [" "].concat(preHeaderRow) : preHeaderRow;
              toSave.push(headerRow);
            }
            let preRow = row.slice(initialCol, finalCol + 1);
            toSave.push(addRecNums ? [(initialItem.rowId + index + 1).toString()].concat(preRow) : addRowNames && rowNames ? [rowNames[initialItem.rowId + index].toString()].concat(preRow) : preRow);
          });
        }
      }
    }
    return ClipboardUtils.encodeCellsForExcel(toSave);
  }
}
_defineProperty(DataGridUtils, "sbSize", void 0);
_defineProperty(DataGridUtils, "LOGGER", LogUtils.getLogger(__filename));
//# sourceMappingURL=DataGridUtils.js.map
